// @flow

import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import Box from 'sail/Box';
import {Button, ButtonGroup} from 'sail/Button';
import Checkbox from 'sail/Checkbox';
import Group from 'sail/Group';
import {TextArea} from 'sail/Input';
import {Title} from 'sail/Text';
import {useAccount, useAnalytics, useApi, useArticle} from 'docs/data';
import {DocsIconAssets} from 'docs/components';
import {sanitizePII} from 'docs/util';
import {defineMessages, FormattedMessage} from 'react-intl';

const FORM_STATE = Object.freeze({
  INITIAL: 'initial',
  SHOW_HELPFUL: 'show-helpful',
  SHOW_UNHELPFUL: 'show-unhelpful',
  SUBMITTING: 'submitting',
  DID_SUBMIT: 'did-submit',
});

const messages = defineMessages({
  mostHelpful: {
    id: 'docsShell.csat.mostHelpful',
    description: 'CSAT question',
    defaultMessage: 'What was most helpful?',
  },
  improve: {
    id: 'docsShell.csat.improve',
    description: 'CSAT question',
    defaultMessage: 'What can we improve?',
  },
  okToFollowUp: {
    id: 'docsShell.csat.okToFollowUp',
    description: 'Asking if ok to follow up on CSAT responses by email',
    defaultMessage: "Yes, it's okay to follow up by email.",
  },
  submit: {
    id: 'docsShell.csat.submit',
    description: 'Label on submit button',
    defaultMessage: 'Submit',
  },
  pageHelpful: {
    id: 'docsShell.csat.pageHelpful',
    description: 'Was this page helpful widget',
    defaultMessage: 'Was this page helpful?',
  },
  yes: {
    id: 'docsShell.csat.yes',
    description: 'Label for yes button',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'docsShell.csat.no',
    description: 'Label for no button',
    defaultMessage: 'No',
  },
  thankYouMessage: {
    id: 'docsShell.csat.thankYouMessage',
    description: 'Thank you message',
    defaultMessage: "Thank you for helping improve Stripe's documentation!",
  },
});

type FormState = $Values<typeof FORM_STATE>;

type CsatProps = {|
  formState: FormState,
  setFormState: (FormState) => void,
  event: ?string,
  setEvent: (string) => void,
|};

const FeedbackForm = (props: CsatProps) => {
  const {formState, setFormState, event} = props;
  const helpful = formState === FORM_STATE.SHOW_HELPFUL;
  const [text, setText] = useState('');
  const [canContact, setCanContact] = useState(false);
  const [pending, setPending] = useState(false);
  const {account} = useAccount();
  const {trackAction} = useAnalytics();
  const {article} = useArticle();
  const {api} = useApi();
  const title =
    formState === FORM_STATE.SHOW_HELPFUL ? (
      <FormattedMessage {...messages.mostHelpful} />
    ) : (
      <FormattedMessage {...messages.improve} />
    );

  const handleSubmit = async () => {
    if (text.trim().length < 1) {
      return;
    }

    const params = {
      helpful,
      text: sanitizePII(text, '[REDACTED]'),
      path: window.location.pathname,
      contact: canContact,
      referrer: document.referrer,
      translated: article.intl.translated,
      locale: article.intl.locale,
    };

    setPending(true);
    await api.sendCsatFeedback(params);
    trackAction('docs_footer_csat_text', {
      ...params,
      poll: event,
    });
    setPending(false);
    setFormState(FORM_STATE.DID_SUBMIT);
    setTimeout(() => {
      setFormState(FORM_STATE.INITIAL);
    }, 5000);
  };

  return (
    <Box margin={{vertical: 20}}>
      <Group spacing={16}>
        <Title style={{fontWeight: 700}}>{title}</Title>
        <TextArea
          onChange={(val) => {
            setText(val);
          }}
          placeholder=""
          resizeable={false}
          value={text}
          width="maximized"
        />
        <Group
          direction="horizontal"
          spacing={32}
          alignItems="center"
          justifyContent="space-between"
        >
          {account.user && account.user.email && (
            <Checkbox
              checked={canContact}
              onChange={({target}) => {
                setCanContact(target.checked);
              }}
              label={<FormattedMessage {...messages.okToFollowUp} />}
            />
          )}
          <Button
            label={<FormattedMessage {...messages.submit} />}
            color="white"
            onClick={handleSubmit}
            pending={pending}
          />
        </Group>
      </Group>
    </Box>
  );
};

const Prompt = (props: CsatProps) => {
  const {setFormState, setEvent} = props;
  const {trackAction} = useAnalytics();

  const handleClick = (formState) => {
    const helpful = formState === FORM_STATE.SHOW_HELPFUL;
    const event = trackAction('docs_footer_csat', {helpful});
    setEvent(event.id);
    setFormState(formState);
  };

  return (
    <Box flex={{direction: 'row'}} padding={{vertical: 24}}>
      <Group direction="horizontal" alignItems="center">
        <Title style={{fontWeight: 700}}>
          <FormattedMessage {...messages.pageHelpful} />
        </Title>
        <Box margin={{left: 20}}>
          <ButtonGroup>
            <Button
              icon={DocsIconAssets.general.thumbsup}
              color="blue"
              label={<FormattedMessage {...messages.yes} />}
              onClick={(evt) => {
                handleClick(FORM_STATE.SHOW_HELPFUL);
              }}
            />
            <Button
              icon={DocsIconAssets.general.thumbsdown}
              color="blue"
              label={<FormattedMessage {...messages.no} />}
              onClick={(evt) => {
                handleClick(FORM_STATE.SHOW_UNHELPFUL);
              }}
            />
          </ButtonGroup>
        </Box>
      </Group>
    </Box>
  );
};

const CsatContent = (props: CsatProps) => {
  switch (props.formState) {
    case FORM_STATE.INITIAL:
      return <Prompt {...props} />;
    case FORM_STATE.DID_SUBMIT:
      return <ThankYouMessage {...props} />;
    default:
      return <FeedbackForm {...props} />;
  }
};

const ThankYouMessage = (props: CsatProps) => {
  return (
    <Box margin={{vertical: 20}} flex={{justifyContent: 'center'}}>
      <Title>
        <FormattedMessage {...messages.thankYouMessage} />
      </Title>
    </Box>
  );
};

export const Csat = () => {
  const [formState, setFormState] = useState(FORM_STATE.INITIAL);
  const [event, setEvent] = useState('');

  return (
    <AnimatePresence initial={false} exitBeforeEnter>
      <motion.div
        key={formState}
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
      >
        <CsatContent
          formState={formState}
          setFormState={setFormState}
          event={event}
          setEvent={setEvent}
        />
      </motion.div>
    </AnimatePresence>
  );
};
